import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  GroupsListResponse,
  GroupType,
  GroupsFilterParams,
  CreateGroupParams,
  UpdateGroupParams,
  PermissionsListResponse,
  PermissionsFilterParams
} from '@/types/group'
import { BulkIdParams, BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { RoleEnum, SortEnum } from '@/enums'

// API functions
import {
  fetchGroups,
  fetchGroup,
  createGroup,
  updateGroup,
  deleteGroup,
  bulkDeleteGroups,
  fetchPermissions
} from '@/lib/api'

type GroupsListStateType = {
  data: GroupsListResponse | null
  isLoading: boolean
  error: string | null
}

type GroupStateType = {
  data: GroupType | null
  isLoading: boolean
  error: {
    status?: number
    message: string
  } | null
}

type PermissionsStateType = {
  data: PermissionsListResponse | null
  isLoading: boolean
  error: string | null
}

type GroupsStoreState = {
  // Filters state
  filters: GroupsFilterParams
  setFilters: (filters: Partial<GroupsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  groupsList: GroupsListStateType
  groupState: GroupStateType
  permissionsState: PermissionsStateType
  selectedGroupIds: string[]

  // API functions
  fetchGroups: (params?: GroupsFilterParams) => Promise<GroupsListResponse>
  resetGroupsList: () => void

  // Single group operations
  getGroup: (groupId: string) => Promise<GroupType>
  createGroup: (params: CreateGroupParams) => Promise<GroupType>
  updateGroup: (groupId: string, params: UpdateGroupParams) => Promise<GroupType>
  deleteGroup: (groupId: string) => Promise<void>
  bulkDeleteGroups: (params: BulkIdParams) => Promise<BulkDeleteResponse>
  resetGroup: () => void

  // Permissions operations
  fetchPermissions: (params?: PermissionsFilterParams) => Promise<PermissionsListResponse>
  resetPermissions: () => void

  // Selection operations
  selectGroup: (id: string) => void
  deselectGroup: (id: string) => void
  toggleGroupSelection: (id: string) => void
  selectAllGroups: (groups: GroupType[]) => void
  deselectAllGroups: () => void
  isGroupSelected: (id: string) => boolean
}

const defaultFilters: GroupsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'createdAt'
}

const initialGroupState: GroupStateType = {
  data: null,
  isLoading: false,
  error: null
}

const useGroupsStore = create<GroupsStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    groupsList: {
      data: null,
      isLoading: false,
      error: null
    },
    groupState: initialGroupState,
    permissionsState: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedGroupIds: [],

    setFilters: newFilters =>
      set(state => ({
        filters: { ...state.filters, ...newFilters }
      })),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: GroupsFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'roles':
            filters[key] = value.split(',') as RoleEnum[]
            break
          case 'sortBy':
          case 'q':
          case 'createdAtStart':
          case 'createdAtEnd':
          case 'updatedAtStart':
          case 'updatedAtEnd':
            filters[key] = value
            break
        }
      })

      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value))
        }
      })

      return params
    },

    fetchGroups: async (params?: GroupsFilterParams) => {
      set(state => ({
        groupsList: {
          ...state.groupsList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchGroups(params || get().filters)

        set(state => ({
          groupsList: {
            ...state.groupsList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({
          groupsList: {
            ...state.groupsList,
            isLoading: false,
            error: errorMessage
          }
        }))
        throw error
      }
    },

    resetGroupsList: () =>
      set(() => ({
        groupsList: {
          data: null,
          isLoading: false,
          error: null
        }
      })),

    getGroup: async (groupId: string) => {
      const currentState = get().groupState
      if (currentState.isLoading) {
        return currentState.data!
      }

      set(() => ({
        groupState: {
          ...initialGroupState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchGroup(groupId)

        set(() => ({
          groupState: {
            ...initialGroupState,
            data
          }
        }))

        return data
      } catch (error: any) {
        const errorMessage = getErrorMessage(error)
        set(() => ({
          groupState: {
            ...initialGroupState,
            error: {
              status: error?.response?.status,
              message: errorMessage
            }
          }
        }))
        throw error
      }
    },

    createGroup: async (params: CreateGroupParams) => {
      try {
        return await createGroup(params)
      } catch (error) {
        throw error
      }
    },

    updateGroup: async (groupId: string, params: UpdateGroupParams) => {
      try {
        return await updateGroup(groupId, params)
      } catch (error) {
        throw error
      }
    },

    deleteGroup: async (groupId: string) => {
      try {
        await deleteGroup(groupId)
      } catch (error) {
        throw error
      }
    },

    bulkDeleteGroups: async (params: BulkIdParams) => {
      try {
        return await bulkDeleteGroups(params)
      } catch (error) {
        throw error
      }
    },

    resetGroup: () =>
      set(() => ({
        groupState: initialGroupState
      })),

    fetchPermissions: async (params?: PermissionsFilterParams) => {
      set(state => ({
        permissionsState: {
          ...state.permissionsState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchPermissions(params)

        set(state => ({
          permissionsState: {
            ...state.permissionsState,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({
          permissionsState: {
            ...state.permissionsState,
            isLoading: false,
            error: errorMessage
          }
        }))
        throw error
      }
    },

    resetPermissions: () =>
      set(() => ({
        permissionsState: {
          data: null,
          isLoading: false,
          error: null
        }
      })),

    selectGroup: id =>
      set(state => ({
        selectedGroupIds: [...state.selectedGroupIds, id]
      })),

    deselectGroup: id =>
      set(state => ({
        selectedGroupIds: state.selectedGroupIds.filter(groupId => groupId !== id)
      })),

    toggleGroupSelection: id => {
      const { selectedGroupIds } = get()
      if (selectedGroupIds.includes(id)) {
        get().deselectGroup(id)
      } else {
        get().selectGroup(id)
      }
    },

    selectAllGroups: groups => {
      const allIds = groups.map(group => group.id)
      set({ selectedGroupIds: allIds })
    },

    deselectAllGroups: () => set({ selectedGroupIds: [] }),

    isGroupSelected: id => get().selectedGroupIds.includes(id)
  }))
)

export default useGroupsStore
